import { useState, useEffect, useCallback, useMemo } from 'react';
import { get, post } from '../api';

// Offer & Candidate Emails — Stage 1 (mockup: ~/Downloads/offer-automation_1.html).
// AI-assisted email composer + offer-letter builder/live-preview + the
// accept→booking automation visual. GUARDRAIL: nothing is auto-sent; "Record"
// advances the pipeline stage and logs an audit note — live transmission is a
// later, manager-triggered step. A Copy button lets the manager paste into the
// mailer in the meantime.
const C = {
  bg: '#0a0702', card: '#1a1208', card2: '#2a1f0f', line: '#3a2a12', text: '#f5e6c8',
  muted: '#8a7055', muted2: '#5a4030', gold: '#c9a96e', green: '#10b981', indigo: '#a5b4fc',
};
const ACTIONS = [
  { k: 'offer', l: 'Send offer (applied role)' },
  { k: 'offer_diff', l: 'Offer a different role' },
  { k: 'second', l: 'Request 2nd interview' },
  { k: 'hired', l: 'Confirm hired' },
  { k: 'no', l: 'Not moving forward' },
];
const ROLES = ['Guest Experience Team Member', 'Bartender', 'Barback', 'Server', 'Cook'];
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmt(d) { return MO[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear(); }
function plusDays(n) { const d = new Date(); d.setDate(d.getDate() + n); return fmt(d); }
function payFor(role) {
  const r = (role || '').toLowerCase();
  if (r.includes('guest experience') || r === 'getm') return '20.00';
  if (r.includes('bartender') || r.includes('barback') || r.includes('bar back') || r.includes('server') || r.includes('cook')) return '18.05';
  return '';
}
function defaultFields(role) {
  return {
    title: role || '', start: plusDays(14), returnby: plusDays(7),
    reports: 'Max Tuttleman, General Manager', street: '', city: 'Fremont, CA 94538',
    type: 'Part-Time', basis: 'Hourly', comp: payFor(role), profile: 'Standard',
    checks: { bg: false, auth: true },
    ben: { sick: true, holidays: false, pto: false, health: false, healthDays: '90', other: false, otherText: '' },
    conditions: '',
  };
}

const inS = { width: '100%', background: C.card2, border: '1px solid ' + C.line, borderRadius: 8, color: C.text, font: 'inherit', fontSize: 13, padding: '8px 10px', boxSizing: 'border-box', fontFamily: 'inherit' };
const lblS = { display: 'block', fontSize: 11, color: C.muted, margin: '10px 0 4px', fontWeight: 600, textTransform: 'none', letterSpacing: 0 };

export function OffersView() {
  const [cands, setCands] = useState([]);
  const [selId, setSelId] = useState(null);
  const [action, setAction] = useState('offer');
  const [tone, setTone] = useState('warm');
  const [offeredRole, setOfferedRole] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [edited, setEdited] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(true);
  const [fields, setFields] = useState(defaultFields(''));
  const [booking, setBooking] = useState('https://teeandtimber.com/book/onboarding');
  const [accepted, setAccepted] = useState(false);
  const [toast, setToast] = useState('');
  const [recordBusy, setRecordBusy] = useState(false);
  const [signLink, setSignLink] = useState('');

  const sel = useMemo(() => cands.find(c => c.id === selId) || null, [cands, selId]);

  useEffect(() => {
    get('/hiring/candidates').then(list => {
      const arr = Array.isArray(list) ? list : [];
      setCands(arr);
      if (arr.length && !selId) setSelId(arr[0].id);
    }).catch(() => {});
  }, []);

  const ctx = useCallback(() => {
    if (!sel) return {};
    return { first: (sel.name || '').split(' ')[0] || sel.name, name: sel.name, role: fields.title, pay: fields.comp, start: fields.start, email: sel.email || '' };
  }, [sel, fields]);

  // Load the template body for the current action+tone (no AI, just merge).
  const loadTemplate = useCallback(async (act, tn) => {
    if (!sel) return;
    try {
      const r = await post('/offers/compose', { action: act, tone: tn, ctx: ctx() });
      setSubject(r.subject); setBody(r.body); setEdited(false);
      if (typeof r.ai_available === 'boolean') setAiAvailable(r.ai_available);
    } catch { /* ignore */ }
  }, [sel, ctx]);

  // When candidate changes: pull prefill context, reset role + fields + draft.
  useEffect(() => {
    if (!selId) return;
    setAccepted(false);
    get('/offers/context/' + selId).then(d => {
      const role = (d.candidate && d.candidate.role) || '';
      setOfferedRole(role);
      const f = defaultFields(role);
      if (d.suggested) { f.title = d.suggested.title || role; f.comp = d.suggested.comp || f.comp; f.reports = d.suggested.reports || f.reports; f.city = d.suggested.city || f.city; }
      setFields(f);
      if (d.booking_link) setBooking(d.booking_link);
    }).catch(() => {});
  }, [selId]);

  // Reload the draft whenever candidate / action / fields-affecting-merge change.
  useEffect(() => {
    if (sel && !edited) loadTemplate(action, tone);
  }, [selId, action, fields.title, fields.comp, fields.start]);

  function pickAction(a) {
    setAction(a); setAccepted(false); setEdited(false);
    if (a === 'offer' && sel) { setOfferedRole(sel.role || ''); }
  }
  function setRole(r) { setOfferedRole(r); setField('title', r); setField('comp', payFor(r) || fields.comp); }
  function setField(k, v) { setFields(p => ({ ...p, [k]: v })); }
  function setBen(k, v) { setFields(p => ({ ...p, ben: { ...p.ben, [k]: v } })); }
  function tog(g, k) { setFields(p => ({ ...p, [g]: { ...p[g], [k]: !p[g][k] } })); }

  async function aiAssist(tn, reset) {
    if (!sel) return;
    const nextTone = reset ? 'warm' : tn;
    setTone(nextTone);
    if (reset) { await loadTemplate(action, 'warm'); flash('↩ Reset to default'); return; }
    if (!aiAvailable) { await loadTemplate(action, nextTone); flash('Tone: ' + nextTone); return; }
    setAiBusy(true);
    try {
      const r = await post('/offers/compose', { action, tone: nextTone, ctx: ctx(), draft: body });
      setBody(r.body); setEdited(false);
      flash(r.ai ? '✨ Reworded — ' + nextTone : 'Tone: ' + nextTone);
    } catch { await loadTemplate(action, nextTone); flash('Tone: ' + nextTone); }
    setAiBusy(false);
  }

  async function record() {
    if (!sel) return;
    setRecordBusy(true);
    try {
      const r = await post('/offers/record', { candidate_id: sel.id, action, subject });
      setCands(cs => cs.map(c => c.id === sel.id ? { ...c, stage: r.stage } : c));
      flash('✓ Recorded — stage: ' + r.stage + ' · email NOT auto-sent');
    } catch (e) { flash('Could not record'); }
    setRecordBusy(false);
  }
  async function copyEmail() {
    const txt = 'Subject: ' + subject + '\n\n' + body;
    try { await navigator.clipboard.writeText(txt); flash('📋 Email copied'); }
    catch { flash('Copy failed — select the text manually'); }
  }
  // Fetch the rendered offer letter (binary) with auth — the shared api() helper
  // only handles JSON, so download/print go straight through fetch.
  async function fetchLetter(format) {
    const token = localStorage.getItem('tnt_token');
    const res = await fetch((process.env.REACT_APP_API_URL || '') + '/api/offers/letter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      body: JSON.stringify({ candidate_id: sel.id, fields, format }),
    });
    if (!res.ok) throw new Error('letter failed');
    return res.blob();
  }
  async function downloadLetter() {
    if (!sel) return;
    try {
      const url = URL.createObjectURL(await fetchLetter('word'));
      const a = document.createElement('a');
      a.href = url; a.download = 'Offer_' + (sel.name || 'candidate').replace(/[^a-z0-9]+/gi, '_') + '.doc';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      flash('⬇ Offer letter downloaded (Word)');
    } catch { flash('Download failed'); }
  }
  async function printLetter() {
    if (!sel) return;
    try { window.open(URL.createObjectURL(await fetchLetter('html')), '_blank'); flash('🖨 Print view opened — use Save as PDF'); }
    catch { flash('Could not open print view'); }
  }
  // Generate a candidate-facing e-sign link (does NOT email — paste it into the offer).
  async function createSigningLink() {
    if (!sel) return;
    try {
      const r = await post('/offers/create-signing', { candidate_id: sel.id, fields });
      try { await navigator.clipboard.writeText(r.url); } catch {}
      setSignLink(r.url);
      flash('🔗 Signing link created & copied — paste it into the offer email');
    } catch { flash('Could not create signing link'); }
  }

  let toastT;
  function flash(m) { setToast(m); clearTimeout(toastT); toastT = setTimeout(() => setToast(''), 2600); }

  const showOffer = action === 'offer' || action === 'offer_diff';
  const aiMark = edited ? '· edited' : (tone !== 'warm' ? '· ' + tone : '');

  return (
    <div style={{ fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: C.text, margin: 0 }}>Offer &amp; Candidate Emails</h2>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: '#34d399', border: '1px solid #10b98155', background: '#10b9811a', borderRadius: 999, padding: '2px 8px' }}>🔒 MANAGER</span>
      </div>
      <p style={{ color: C.muted, fontSize: 13, marginTop: 4, marginBottom: 18 }}>Prefilled, editable emails with AI assist · offer-letter merge · the accept → booking flow. <b style={{ color: C.gold }}>Nothing is auto-sent</b> — record advances the pipeline; copy to send from your mailer.</p>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* LEFT: candidate + action */}
        <div style={{ flex: '0 0 270px', maxWidth: 270 }}>
          <Card title="Candidate">
            <div style={{ maxHeight: 280, overflowY: 'auto', margin: '0 -4px' }}>
              {cands.length === 0 && <p style={{ color: C.muted2, fontSize: 13 }}>No candidates yet.</p>}
              {cands.map(p => (
                <div key={p.id} onClick={() => setSelId(p.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 9px', borderRadius: 9, cursor: 'pointer', border: '1px solid ' + (p.id === selId ? C.line : 'transparent'), background: p.id === selId ? '#1f1709' : 'transparent' }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: C.card2, color: C.gold, fontWeight: 800, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{(p.name || '?').split(' ').map(x => x[0]).join('').slice(0, 2)}</div>
                  <div style={{ minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 13.5, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div><div style={{ fontSize: 11.5, color: C.muted }}>Applied: {p.role || '—'}</div></div>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Action">
            {ACTIONS.map(a => (
              <button key={a.k} onClick={() => pickAction(a.k)} style={{ display: 'block', width: '100%', textAlign: 'left', background: a.k === action ? '#1f1709' : C.card2, border: '1px solid ' + (a.k === action ? C.gold : C.line), color: a.k === action ? C.gold : C.text, borderRadius: 9, padding: '9px 11px', font: 'inherit', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', marginBottom: 7, fontFamily: 'inherit' }}>{a.l}</button>
            ))}
          </Card>
        </div>

        {/* MIDDLE: email + automation */}
        <div style={{ flex: '1 1 380px', minWidth: 340 }}>
          <Card title={<>Email {aiMark && <span style={{ color: C.indigo, textTransform: 'none', letterSpacing: 0, fontWeight: 600, fontSize: 12 }}>{aiMark}</span>}</>}>
            <label style={lblS}>To</label>
            <input readOnly value={sel ? (sel.email || '(no email on file)') : ''} style={{ ...inS, color: C.muted }} />
            <label style={lblS}>Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={inS} />
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', margin: '10px 0', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: C.muted }}>✨ AI assist:</span>
              {[['warm', 'Warmer'], ['concise', 'More concise'], ['formal', 'More formal']].map(([t, l]) => (
                <button key={t} disabled={aiBusy} onClick={() => aiAssist(t)} style={pillBtn(false, aiBusy)}>{l}</button>
              ))}
              <button disabled={aiBusy} onClick={() => aiAssist('warm', true)} style={pillBtn(true, aiBusy)}>Reset</button>
              {aiBusy && <span style={{ fontSize: 12, color: C.indigo }}>rewording…</span>}
            </div>
            <textarea value={body} onChange={e => { setBody(e.target.value); setEdited(true); }} style={{ ...inS, minHeight: 220, resize: 'vertical', lineHeight: 1.5, fontSize: 13 }} />
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Fully editable — type over anything. {aiAvailable ? 'AI assist is a live model call.' : 'AI assist not configured (set ANTHROPIC_API_KEY) — tone buttons use templates.'} Merge fields fill from the candidate.</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button onClick={record} disabled={!sel || recordBusy} style={{ background: C.gold, color: '#1a1208', border: 'none', borderRadius: 9, font: 'inherit', fontWeight: 800, fontSize: 13.5, padding: '9px 16px', cursor: 'pointer', opacity: (!sel || recordBusy) ? 0.5 : 1, fontFamily: 'inherit' }}>✓ Record &amp; advance stage</button>
              <button onClick={copyEmail} disabled={!sel} style={{ background: C.card2, border: '1px solid ' + C.line, color: C.text, borderRadius: 9, font: 'inherit', fontWeight: 700, fontSize: 13, padding: '9px 14px', cursor: 'pointer', fontFamily: 'inherit' }}>📋 Copy email</button>
            </div>
          </Card>

          {showOffer && (
            <Card title="Automation">
              <Flow sel={sel} action={action} offeredRole={offeredRole} tone={tone} edited={edited} accepted={accepted} booking={booking} onAccept={() => { setAccepted(true); flash('✓ Accepted (simulated) → booking step lights up'); }} />
            </Card>
          )}
        </div>

        {/* RIGHT: offer letter builder + preview */}
        {showOffer && (
          <div style={{ flex: '1 1 360px', minWidth: 320 }}>
            <Card title="Offer letter — auto-filled">
              <div style={{ fontSize: 12, color: C.muted, margin: '0 0 8px' }}>Auto-filled from the record — every field is editable.</div>
              {action === 'offer_diff'
                ? (<><label style={lblS}>Offered position (internal)</label><select value={offeredRole} onChange={e => setRole(e.target.value)} style={inS}>{(ROLES.includes(offeredRole) ? ROLES : [offeredRole, ...ROLES]).map(r => <option key={r} value={r}>{r}</option>)}</select></>)
                : (<><label style={lblS}>Position (as applied, internal)</label><input readOnly value={offeredRole} style={{ ...inS, color: C.muted }} /></>)}
              <label style={lblS}>Job title (what the candidate sees)</label>
              <input value={fields.title} onChange={e => setField('title', e.target.value)} style={inS} />
              <Row>
                <Field label="Start date" value={fields.start} onChange={v => setField('start', v)} />
                <Field label="Offer return-by" value={fields.returnby} onChange={v => setField('returnby', v)} />
              </Row>
              <label style={lblS}>Reports to</label>
              <input value={fields.reports} onChange={e => setField('reports', e.target.value)} style={inS} />
              <Row>
                <Field label="Street address" value={fields.street} onChange={v => setField('street', v)} />
                <Field label="City, State ZIP" value={fields.city} onChange={v => setField('city', v)} />
              </Row>
              <Row>
                <SelField label="Employment type" value={fields.type} opts={['Part-Time', 'Full-Time']} onChange={v => setField('type', v)} />
                <SelField label="Pay basis" value={fields.basis} opts={['Hourly', 'Salary']} onChange={v => setField('basis', v)} />
              </Row>
              <Row>
                <Field label="Starting comp ($)" value={fields.comp} onChange={v => setField('comp', v)} />
                <SelField label="Benefit profile" value={fields.profile} opts={['Standard', 'None', 'Custom']} onChange={v => setField('profile', v)} />
              </Row>
              <label style={lblS}>Pre-employment checks</label>
              <Check label="Background check" on={fields.checks.bg} onClick={() => tog('checks', 'bg')} />
              <Check label="Work authorization verification" on={fields.checks.auth} onClick={() => tog('checks', 'auth')} />
              <label style={lblS}>Benefits selection</label>
              <Check label="Paid sick leave" on={fields.ben.sick} onClick={() => setBen('sick', !fields.ben.sick)} />
              <Check label="Paid holidays" on={fields.ben.holidays} onClick={() => setBen('holidays', !fields.ben.holidays)} />
              <Check label="Paid time off / vacation" on={fields.ben.pto} onClick={() => setBen('pto', !fields.ben.pto)} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Check label="Health insurance after" on={fields.ben.health} onClick={() => setBen('health', !fields.ben.health)} />
                <input value={fields.ben.healthDays} onChange={e => setBen('healthDays', e.target.value)} style={{ ...inS, width: 55 }} /><span style={{ fontSize: 13, color: C.muted }}>days</span>
              </div>
              <Check label="Other" on={fields.ben.other} onClick={() => setBen('other', !fields.ben.other)} />
              {fields.ben.other && <input placeholder="Describe other benefit" value={fields.ben.otherText} onChange={e => setBen('otherText', e.target.value)} style={inS} />}
              <label style={lblS}>Other notes / conditions</label>
              <textarea value={fields.conditions} onChange={e => setField('conditions', e.target.value)} style={{ ...inS, minHeight: 54, resize: 'vertical' }} />
              <div style={{ fontSize: 12, color: C.muted, margin: '12px 0 8px' }}>Live preview · <span style={{ background: '#fff7e6', color: '#7a5b12', padding: '0 4px', borderRadius: 3 }}>yellow</span> = prefilled from the record.</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <button onClick={downloadLetter} disabled={!sel} style={{ background: C.card2, border: '1px solid ' + C.gold, color: C.gold, borderRadius: 9, font: 'inherit', fontWeight: 700, fontSize: 13, padding: '8px 14px', cursor: 'pointer', fontFamily: 'inherit' }}>⬇ Download letter (Word)</button>
                <button onClick={printLetter} disabled={!sel} style={{ background: C.card2, border: '1px solid ' + C.line, color: C.text, borderRadius: 9, font: 'inherit', fontWeight: 700, fontSize: 13, padding: '8px 14px', cursor: 'pointer', fontFamily: 'inherit' }}>🖨 Print / Save as PDF</button>
                <button onClick={createSigningLink} disabled={!sel} style={{ background: '#10b9811a', border: '1px solid #10b98155', color: '#34d399', borderRadius: 9, font: 'inherit', fontWeight: 700, fontSize: 13, padding: '8px 14px', cursor: 'pointer', fontFamily: 'inherit' }}>🔗 Create e-sign link</button>
              </div>
              {signLink && (
                <div style={{ background: C.card2, border: '1px solid ' + C.line, borderRadius: 9, padding: '8px 10px', marginBottom: 10, fontSize: 12 }}>
                  <div style={{ color: C.muted, marginBottom: 4 }}>Candidate signing link (copied — paste it into the offer email):</div>
                  <div style={{ color: C.gold, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 11.5 }}>{signLink}</div>
                </div>
              )}
              <Letter sel={sel} fields={fields} />
            </Card>
          </div>
        )}
      </div>

      {toast && <div style={{ position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', background: C.card2, border: '1px solid ' + C.gold, color: C.text, padding: '10px 16px', borderRadius: 10, fontSize: 13.5, fontWeight: 600, zIndex: 2000, boxShadow: '0 8px 30px rgba(0,0,0,.5)' }}>{toast}</div>}
    </div>
  );
}

function Card({ title, children }) {
  return <div style={{ background: C.card, border: '1px solid ' + C.line, borderRadius: 12, padding: '15px 17px', marginBottom: 14 }}>
    <div style={{ fontSize: 12, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, marginBottom: 11, fontWeight: 600 }}>{title}</div>
    {children}
  </div>;
}
function Row({ children }) { return <div style={{ display: 'flex', gap: 10 }}>{[].concat(children).map((c, i) => <div key={i} style={{ flex: 1, minWidth: 0 }}>{c}</div>)}</div>; }
function Field({ label, value, onChange }) { return <><label style={lblS}>{label}</label><input value={value} onChange={e => onChange(e.target.value)} style={inS} /></>; }
function SelField({ label, value, opts, onChange }) { return <><label style={lblS}>{label}</label><select value={value} onChange={e => onChange(e.target.value)} style={inS}>{opts.map(o => <option key={o} value={o}>{o}</option>)}</select></>; }
function Check({ label, on, onClick }) {
  return <label onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: C.text, margin: '5px 0', cursor: 'pointer', fontWeight: 500 }}>
    <input type="checkbox" readOnly checked={on} style={{ width: 'auto' }} />{label}
  </label>;
}
function pillBtn(reset, busy) {
  return { background: reset ? C.card2 : '#6366f11a', border: '1px solid ' + (reset ? C.line : '#6366f144'), color: reset ? C.muted : C.indigo, borderRadius: 999, font: 'inherit', fontSize: 12, fontWeight: 700, padding: '5px 11px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1, fontFamily: 'inherit' };
}

function Flow({ sel, action, offeredRole, tone, edited, accepted, booking, onAccept }) {
  if (!sel) return null;
  const diff = action === 'offer_diff' && offeredRole !== sel.role;
  const steps = [];
  steps.push({ t: 'Offer email composed', d: tone + (edited ? ' · edited' : '') + ' tone', s: 'done' });
  steps.push({ t: 'Recorded → stage: Offer', d: 'Candidate would get an accept link', s: accepted ? 'done' : 'active' });
  if (diff) steps.push({ t: 'Different role offered', d: 'Applied: ' + (sel.role || '—') + ' → Offered: ' + offeredRole, s: accepted ? 'done' : 'pending' });
  steps.push({ t: accepted ? 'Candidate accepted' : 'Awaiting acceptance', d: accepted ? 'Acceptance recorded' : '—', s: accepted ? 'done' : 'pending' });
  steps.push({ t: 'Booking email auto-sent', d: accepted ? 'Onboarding link delivered → Bookings calendar' : 'Fires on acceptance (later stage)', s: accepted ? 'done' : 'pending' });
  const dot = s => ({ position: 'absolute', left: -19, top: 8, width: 12, height: 12, borderRadius: '50%', border: '2px solid ' + C.card, background: s === 'done' ? '#34d399' : s === 'active' ? C.gold : C.muted2 });
  return (
    <div style={{ position: 'relative', paddingLeft: 22 }}>
      <div style={{ position: 'absolute', left: 6, top: 6, bottom: 6, width: 2, background: C.line }} />
      {steps.map((st, i) => (
        <div key={i} style={{ position: 'relative', padding: '6px 0 12px' }}>
          <div style={dot(st.s)} />
          <div style={{ fontWeight: 700, fontSize: 13.5, color: C.text }}>{st.t}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{st.d}</div>
        </div>
      ))}
      {!accepted && <button onClick={onAccept} style={{ background: C.card2, border: '1px solid ' + C.line, color: C.text, borderRadius: 9, font: 'inherit', fontWeight: 700, fontSize: 13, padding: '7px 12px', cursor: 'pointer', marginTop: 4, fontFamily: 'inherit' }}>▶ Simulate: candidate accepts</button>}
      {accepted && <div style={{ marginTop: 10, padding: '11px 13px', background: '#10b9811a', border: '1px solid #10b98144', borderRadius: 9, fontSize: 13 }}><b>Booking email (preview) →</b><br />"Hi {(sel.name || '').split(' ')[0]}, welcome aboard! Book your onboarding here: {booking}"</div>}
    </div>
  );
}

function Letter({ sel, fields }) {
  if (!sel) return null;
  const F = x => <span style={{ background: '#fff7e6', padding: '0 2px', borderBottom: '1px dashed #c9a96e', fontWeight: 600 }}>{x}</span>;
  const box = on => on ? '☑' : '☐';
  const bens = [];
  if (fields.ben.sick) bens.push('Paid sick leave');
  if (fields.ben.holidays) bens.push('Paid holidays');
  if (fields.ben.pto) bens.push('Paid time off / vacation');
  if (fields.ben.health) bens.push('Health insurance after ' + fields.ben.healthDays + ' days');
  if (fields.ben.other && fields.ben.otherText) bens.push(fields.ben.otherText);
  return (
    <div style={{ background: '#fff', color: '#1a1a1a', borderRadius: 8, padding: '24px 28px', fontFamily: 'Georgia, serif', fontSize: 13, lineHeight: 1.6, maxHeight: 400, overflow: 'auto' }}>
      <div style={{ fontSize: 17, color: '#1F4E79', fontWeight: 700, marginBottom: 3 }}>TEE &amp; TIMBER</div>
      <div style={{ color: '#888', fontSize: 11.5 }}>Chea Enterprises LLC, DBA Tee &amp; Timber · 40480 Albrae St, Fremont, CA 94538</div>
      <br />{fmt(new Date())}<br /><br />
      {F(sel.name)}<br />{fields.street ? <>{F(fields.street)}<br /></> : null}{fields.city ? <>{F(fields.city)}<br /></> : null}<br />
      Dear {F(sel.name)},<br /><br />
      We are pleased to offer you the position of {F(fields.title)} at Tee &amp; Timber, a {F(fields.type)} role reporting to {F(fields.reports)}.<br /><br />
      Your starting compensation is {F('$' + fields.comp)} ({F(fields.basis)}), with a scheduled start date of {F(fields.start)}. This offer is open until {F(fields.returnby)}.<br /><br />
      <b>Pre-employment checks:</b> {box(fields.checks.bg)} Background check &nbsp;&nbsp; {box(fields.checks.auth)} Work authorization verification<br />
      <b>Benefits:</b> {F(bens.length ? bens.join(', ') : 'None selected')}{fields.conditions ? <><br /><b>Conditions:</b> {F(fields.conditions)}</> : null}<br /><br />
      Tee &amp; Timber participates in E-Verify. On your first day, please bring original documents to complete Form I-9 (one from List A, or one each from Lists B and C). RBS and Food Handler certifications are company-paid and due within 30 days of hire. Training time is compensated at $18.05/hr.<br /><br />
      We're excited to welcome you to the team.<br /><br />Sincerely,<br />Max Tuttleman<br />General Manager, Tee &amp; Timber
    </div>
  );
}
